import { Box, Container, Typography, createStyles, makeStyles } from '@material-ui/core';
import React, { useCallback, useMemo } from 'react';
import { KeyboardArrowRight } from '@material-ui/icons';

import { TranquiTheme } from '../../branding/tranquifinanzas/theme';
import { BankType, BrandType, DebtManager, ManagedBank } from '../../generated/graphql';
import { getDebtManagerImage } from '../../utils/getBankImage';
import { getBankLabel } from '../../utils/getBankLabel';
import RenderText from '../../dictionaries/RenderText';
import Separator from '../Separator/Separator';
import { session } from '../../utils/auth';

type OtherBankObligationsProps = {
  banks: ManagedBank[];
  onChange: (brand: BrandType, debtManager: DebtManager, bank: BankType) => void;
};

function OtherBankObligations(props: OtherBankObligationsProps) {
  const { banks, onChange } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  const bankList = useMemo(() => {
    return banks
      .filter(({ debtManager }) => debtManager !== session.debtManager)
      .map((bank) => ({
        ...bank,
        id: `${bank.brand}-${bank.debtManager}-${bank.bank}`,
        logo: getDebtManagerImage(bank.debtManager),
        label: getBankLabel(bank.debtManager),
      }));
  }, [ banks ]);

  const handleClick = useCallback((brand: BrandType, debtManager: DebtManager, bank: BankType) => {
    onChange(brand, debtManager, bank);
  }, [ onChange ]);

  if (!bankList.length) return null;

  return (
    <Box className={classes.wrapper}>
      <Separator />
      <Container className={classes.contentWrapper}>
        <Typography className={classes.title}>
          <RenderText textKey="other_bank_obligations_title" />
        </Typography>
        <Typography className={classes.description}>
          <RenderText textKey="other_bank_obligations_description" />
        </Typography>
        <Box className={classes.bankListWrapper}>
          {bankList.map((item) => (
            <Box
              key={item.id}
              className={classes.bankItem}
              onClick={() => handleClick(item.brand, item.debtManager, item.bank)}
            >
              <Box className={classes.bankLogoWrapper}>
                {
                  item.logo
                    ? <img src={item.logo} className={classes.bankLogo} alt={item.label} />
                    : <Typography className={classes.bankLabel}>{item.label}</Typography>
                }
              </Box>
              <Box className={classes.bankContinueWrapper}>
                <Typography className={classes.bankContinue}>Consultar obligaciones</Typography>
                <KeyboardArrowRight color="secondary" />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

// eslint-disable-next-line @typescript-eslint/no-use-before-define
const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  wrapper: {
    margin: theme.spacing(10, 0),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(5),
    },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 2.5),
  },
  title: {
    color: theme.palette.indigo.main,
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
    letterSpacing: -0.8,
    marginTop: theme.spacing(3),
  },
  description: {
    color: theme.palette.text4.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
  },
  bankListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
  bankItem: {
    alignItems: 'center',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[4],
    cursor: 'pointer',
    display: 'flex',
    height: theme.spacing(8),
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
  },
  bankLogoWrapper: {
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 1),
    },
  },
  bankLogo: {
    maxHeight: theme.spacing(3.75), // 30px
    maxWidth: theme.spacing(18), // 144px
    width: '100%',
  },
  bankLabel: {
    color: theme.palette.indigo.main,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
  },
  bankContinueWrapper: {
    alignItems: 'center',
    display: 'flex',
    whiteSpace: 'nowrap',
  },
  bankContinue: {
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 600,
  },
}));

export default OtherBankObligations;
